import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import { SuccessFetchListResponse } from "@/services/api";
import { useDataTableQuery } from "@/components/data-table/hooks/useDataTableQuery";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { ReadProjectSchedule } from "@/types/projectSchedule";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";

interface ProjectSchedulesContextType {
  projectId: string;
  data: ReadProjectSchedule[];
  projectStartDate: Date | undefined;
  projectEndDate: Date | undefined;
  selectedStartDate: Date;
  selectedEndDate: Date;
  setSelectedStartDate: (date: Date) => void;
  setSelectedEndDate: (date: Date) => void;
  refetch: () => void;
  isUpdateDialogOpened: boolean;
  setIsUpdateDialogOpened: (open: boolean) => void;
  isAddDialogOpened: boolean;
  setIsAddDialogOpened: (open: boolean) => void;
  isDeleteDialogOpened: boolean;
  setIsDeleteDialogOpened: (open: boolean) => void;
}

const ProjectSchedulesContext = createContext<
  ProjectSchedulesContextType | undefined
>(undefined);

export const ProjectSchedulesContextProvider = ({
  children,
  setSelectedStartDate,
  setSelectedEndDate,
  selectedStartDate,
  selectedEndDate,
  projectStartDate,
  projectEndDate,
  isUpdateDialogOpened,
  setIsUpdateDialogOpened,
  isAddDialogOpened,
  setIsAddDialogOpened,
  isDeleteDialogOpened,
  setIsDeleteDialogOpened,
}: PropsWithChildren<
  Pick<
    ProjectSchedulesContextType,
    | "setSelectedStartDate"
    | "setSelectedEndDate"
    | "projectStartDate"
    | "projectEndDate"
    | "selectedStartDate"
    | "selectedEndDate"
    | "isUpdateDialogOpened"
    | "setIsUpdateDialogOpened"
    | "isAddDialogOpened"
    | "setIsAddDialogOpened"
    | "isDeleteDialogOpened"
    | "setIsDeleteDialogOpened"
  >
>) => {
  const projectId = useProjectId();
  const { query } = useDataTableQuery();

  const tableQueryTyped = query as UseInfiniteQueryResult<
    InfiniteData<SuccessFetchListResponse<ReadProjectSchedule>>
  >;

  const data = useMemo(
    () =>
      tableQueryTyped.data?.pages.flatMap(({ items }) => items) ||
      ([] as ReadProjectSchedule[]),
    [tableQueryTyped.data?.pages],
  );

  const value = useMemo<ProjectSchedulesContextType>(
    () => ({
      projectId,
      data,
      selectedStartDate,
      selectedEndDate,
      projectStartDate,
      projectEndDate,
      setSelectedStartDate,
      setSelectedEndDate,
      refetch: tableQueryTyped.refetch,
      isUpdateDialogOpened,
      setIsUpdateDialogOpened,
      isAddDialogOpened,
      setIsAddDialogOpened,
      isDeleteDialogOpened,
      setIsDeleteDialogOpened,
    }),
    [
      data,
      selectedStartDate,
      selectedEndDate,
      projectStartDate,
      projectEndDate,
      projectId,
      setSelectedEndDate,
      setSelectedStartDate,
      tableQueryTyped.refetch,
      isUpdateDialogOpened,
      setIsUpdateDialogOpened,
      isAddDialogOpened,
      setIsAddDialogOpened,
      isDeleteDialogOpened,
      setIsDeleteDialogOpened,
    ],
  );

  return (
    <ProjectSchedulesContext.Provider value={value}>
      {children}
    </ProjectSchedulesContext.Provider>
  );
};

export const useProjectSchedulesContext = () => {
  const context = useContext(ProjectSchedulesContext);
  if (!context) {
    throw new Error(
      "useProjectSchedulesContext must be used within a ProjectSchedulesContext",
    );
  }
  return context;
};
